import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { authAPI, industriesAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { 
  User, 
  Mail, 
  Phone, 
  Building, 
  Briefcase, 
  MapPin, 
  Lock, 
  Camera,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  Shield,
  Plus,
  Star,
  Trash2
} from 'lucide-react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { toast } from 'sonner';
import { 
  COUNTRIES, 
  getStatesForCountry, 
  hasStates, 
  lookupZipCode,
  debounce 
} from '@/utils/addressUtils';

export default function UserProfilePage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [zipLoading, setZipLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    industry_id: '',
    bio: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'US',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Email management state
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [addingEmail, setAddingEmail] = useState(false);

  // Track if initial data has been loaded
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Only initialize form data once when user first loads
    // After that, form data is updated by save responses
    if (user && !isInitialized) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        job_title: user.job_title || '',
        industry_id: user.industry_id || '',
        bio: user.bio || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
        country: user.country || 'US',
      });
      setProfileImage(user.profile_image || null);
      // Set emails from user data or create default
      if (user.emails && user.emails.length > 0) {
        setEmails(user.emails);
      } else {
        setEmails([{ email: user.email, is_primary: true, is_verified: user.is_verified }]);
      }
      setIsInitialized(true);
    }
    fetchIndustries();
  }, [user, isInitialized]);

  const fetchIndustries = async () => {
    try {
      const response = await industriesAPI.getAll();
      setIndustries(response.data);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  // Handle country change - reset state when country changes
  const handleCountryChange = (value) => {
    setProfileData(prev => ({ 
      ...prev, 
      country: value,
      state: '' // Reset state when country changes
    }));
  };

  // ZIP code lookup with debounce
  const performZipLookup = useCallback(
    debounce(async (zipCode, countryCode) => {
      if (!zipCode || zipCode.length < 3) return;
      
      setZipLoading(true);
      try {
        const result = await lookupZipCode(zipCode, countryCode);
        if (result) {
          setProfileData(prev => ({
            ...prev,
            city: result.city || prev.city,
            state: result.state || prev.state,
          }));
        }
      } finally {
        setZipLoading(false);
      }
    }, 500),
    []
  );

  // Handle ZIP code change
  const handleZipChange = (value) => {
    setProfileData(prev => ({ ...prev, zip_code: value }));
    // Trigger lookup for supported countries
    const country = profileData.country || 'US';
    if (['US', 'CA', 'DE', 'GB', 'AU', 'BR', 'MX', 'IN'].includes(country)) {
      performZipLookup(value, country);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Remove email from update data as it can't be changed directly
      const { email, ...updateData } = profileData;
      
      const response = await authAPI.updateMe(updateData);
      
      // Update local profile data from response to keep form in sync
      const updatedUser = response.data;
      setProfileData({
        first_name: updatedUser.first_name || '',
        last_name: updatedUser.last_name || '',
        email: updatedUser.email || '',
        phone: updatedUser.phone || '',
        company: updatedUser.company || '',
        job_title: updatedUser.job_title || '',
        industry_id: updatedUser.industry_id || '',
        bio: updatedUser.bio || '',
        address: updatedUser.address || '',
        city: updatedUser.city || '',
        state: updatedUser.state || '',
        zip_code: updatedUser.zip_code || '',
        country: updatedUser.country || 'US',
      });
      
      // Update emails if present
      if (updatedUser.emails && updatedUser.emails.length > 0) {
        setEmails(updatedUser.emails);
      }
      
      // Refresh the auth context user
      await refreshUser();
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Profile update failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await authAPI.updatePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Password updated successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Email management functions
  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setAddingEmail(true);
    try {
      const response = await authAPI.addEmail(newEmail.toLowerCase());
      setEmails(response.data.emails);
      setNewEmail('');
      toast.success('Email added successfully');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add email');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleSetPrimaryEmail = async (email) => {
    setEmailLoading(true);
    try {
      const response = await authAPI.setPrimaryEmail(email);
      setEmails(response.data.emails);
      toast.success('Primary email updated');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set primary email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDeleteEmail = async (email) => {
    if (!window.confirm(`Are you sure you want to remove ${email}?`)) {
      return;
    }
    
    setEmailLoading(true);
    try {
      const response = await authAPI.deleteEmail(email);
      setEmails(response.data.emails);
      toast.success('Email removed');
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setImageUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/profile-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
        },
        body: formData,
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setProfileImage(data.url);
      
      // Update user profile with new image
      await authAPI.updateMe({ profile_image: data.url });
      await refreshUser();
      
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const getInitials = () => {
    const first = profileData.first_name?.[0] || '';
    const last = profileData.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  return (
    <div className="space-y-6" data-testid="user-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-950">My Profile</h1>
          <p className="text-zinc-500 text-sm mt-1">Manage your account settings and preferences</p>
        </div>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-zinc-100 p-1 rounded-xl">
          <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="address" className="rounded-lg data-[state=active]:bg-white">
            <MapPin className="w-4 h-4 mr-2" />
            Address
          </TabsTrigger>
          <TabsTrigger value="security" className="rounded-lg data-[state=active]:bg-white">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Profile Picture Card */}
            <Card className="border-zinc-200 rounded-xl">
              <CardHeader>
                <CardTitle className="text-lg">Profile Picture</CardTitle>
                <CardDescription>Upload a photo to personalize your account</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <div className="relative">
                  <Avatar className="w-32 h-32 border-4 border-zinc-100">
                    <AvatarImage src={profileImage} alt={profileData.first_name} />
                    <AvatarFallback className="text-2xl font-bold bg-zinc-100 text-zinc-950">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <label className="absolute bottom-0 right-0 w-10 h-10 bg-zinc-950 rounded-full flex items-center justify-center cursor-pointer hover:bg-zinc-800 transition-colors shadow-lg">
                    {imageUploading ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={imageUploading}
                    />
                  </label>
                </div>
                <p className="text-xs text-zinc-500 mt-4 text-center">
                  JPG, PNG or GIF. Max 5MB.
                </p>
              </CardContent>
            </Card>

            {/* Profile Info Card */}
            <Card className="border-zinc-200 rounded-xl lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Personal Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="first_name"
                          value={profileData.first_name}
                          onChange={(e) => handleProfileChange('first_name', e.target.value)}
                          className="pl-9 rounded-lg"
                          placeholder="John"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name</Label>
                      <Input
                        id="last_name"
                        value={profileData.last_name}
                        onChange={(e) => handleProfileChange('last_name', e.target.value)}
                        className="rounded-lg"
                        placeholder="Smith"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Addresses</Label>
                    <p className="text-xs text-zinc-500 mb-2">Manage your email addresses. Primary email will receive all notifications.</p>
                    
                    {/* Email List */}
                    <div className="space-y-2 mb-3">
                      {emails.map((emailItem, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            emailItem.is_primary ? 'border-zinc-400 bg-zinc-50' : 'border-zinc-200 bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Mail className={`w-4 h-4 ${emailItem.is_primary ? 'text-zinc-900' : 'text-zinc-400'}`} />
                            <span className="text-sm font-medium">{emailItem.email}</span>
                            {emailItem.is_primary && (
                              <span className="flex items-center gap-1 text-xs bg-zinc-100 text-zinc-950 px-2 py-0.5 rounded-full">
                                <Star className="w-3 h-3" />
                                Primary
                              </span>
                            )}
                            {emailItem.is_verified && (
                              <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                <CheckCircle className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {!emailItem.is_primary && (
                              <>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSetPrimaryEmail(emailItem.email)}
                                  disabled={emailLoading}
                                  className="text-xs text-zinc-900 hover:text-zinc-950"
                                >
                                  Set as Primary
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEmail(emailItem.email)}
                                  disabled={emailLoading}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add New Email */}
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="Add another email address"
                          className="pl-9 rounded-lg"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleAddEmail}
                        disabled={addingEmail || !newEmail.trim()}
                        className="bg-zinc-950 hover:bg-zinc-800"
                      >
                        {addingEmail ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <PhoneInput
                      defaultCountry="us"
                      value={profileData.phone}
                      onChange={(phone) => handleProfileChange('phone', phone)}
                      inputClassName="!w-full !h-10 !rounded-lg !border-zinc-200"
                      countrySelectorStyleProps={{
                        buttonClassName: "!h-10 !rounded-l-lg !border-zinc-200 !bg-zinc-50",
                      }}
                      className="phone-input-custom"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="company"
                          value={profileData.company}
                          onChange={(e) => handleProfileChange('company', e.target.value)}
                          className="pl-9 rounded-lg"
                          placeholder="Company Inc."
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="job_title">Job Title</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="job_title"
                          value={profileData.job_title}
                          onChange={(e) => handleProfileChange('job_title', e.target.value)}
                          className="pl-9 rounded-lg"
                          placeholder="Manager"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry_id">Industry</Label>
                    <SearchableSelect
                      options={industries.map((industry) => ({ value: industry.id, label: industry.name }))}
                      value={profileData.industry_id}
                      onValueChange={(value) => handleProfileChange('industry_id', value)}
                      placeholder="Select your industry"
                      searchPlaceholder="Search industries..."
                      className="rounded-lg"
                      dataTestId="industry-select"
                      preserveOrder={true}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl"
                    disabled={loading}
                    data-testid="save-profile-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Address Tab */}
        <TabsContent value="address">
          <Card className="border-zinc-200 rounded-xl">
            <CardHeader>
              <CardTitle className="text-lg">Address Information</CardTitle>
              <CardDescription>
                Your address will be used for billing and shipping. Updates here will sync with checkout.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                {/* Country - First */}
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select 
                    value={profileData.country} 
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="profile-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* State and ZIP Code */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State / Province</Label>
                    {hasStates(profileData.country) ? (
                      <Select 
                        value={profileData.state} 
                        onValueChange={(value) => handleProfileChange('state', value)}
                      >
                        <SelectTrigger className="rounded-lg" data-testid="profile-state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {getStatesForCountry(profileData.country).map((state) => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="state"
                        value={profileData.state}
                        onChange={(e) => handleProfileChange('state', e.target.value)}
                        className="rounded-lg"
                        placeholder="State/Province"
                        data-testid="profile-state-input"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP / Postal Code</Label>
                    <div className="relative">
                      <Input
                        id="zip_code"
                        value={profileData.zip_code}
                        onChange={(e) => handleZipChange(e.target.value)}
                        className="rounded-lg pr-8"
                        placeholder="ZIP / Postal Code"
                        data-testid="profile-zip-input"
                      />
                      {zipLoading && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-400" />
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">Enter ZIP to auto-fill city & state</p>
                  </div>
                </div>

                {/* City */}
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={profileData.city}
                    onChange={(e) => handleProfileChange('city', e.target.value)}
                    className="rounded-lg"
                    placeholder="City"
                    data-testid="profile-city-input"
                  />
                </div>

                {/* Street Address - Last */}
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="address"
                      value={profileData.address}
                      onChange={(e) => handleProfileChange('address', e.target.value)}
                      className="pl-9 rounded-lg"
                      placeholder="123 Main Street, Apt 4B"
                      data-testid="profile-address-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl"
                  disabled={loading}
                  data-testid="save-address-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Address
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="border-zinc-200 rounded-xl">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="current_password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                      className="pl-9 pr-10 rounded-lg"
                      placeholder="Enter current password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="new_password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                      className="pl-9 pr-10 rounded-lg"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      id="confirm_password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                      className="pl-9 pr-10 rounded-lg"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {passwordData.new_password && (
                  <div className="flex items-center gap-2 text-xs">
                    {passwordData.new_password.length >= 6 ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className={passwordData.new_password.length >= 6 ? 'text-green-600' : 'text-amber-600'}>
                      At least 6 characters
                    </span>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Update Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Custom styles for phone input */}
      <style>{`
        .phone-input-custom .react-international-phone-input-container {
          width: 100%;
        }
        .phone-input-custom .react-international-phone-input {
          width: 100% !important;
          height: 40px !important;
          border-radius: 0 8px 8px 0 !important;
          border-color: #e2e8f0 !important;
          font-size: 14px !important;
        }
        .phone-input-custom .react-international-phone-input:focus {
          border-color: #1B4F72 !important;
          box-shadow: 0 0 0 1px #1B4F72 !important;
        }
        .phone-input-custom .react-international-phone-country-selector-button {
          height: 40px !important;
          border-radius: 8px 0 0 8px !important;
          border-color: #e2e8f0 !important;
          background-color: #f8fafc !important;
        }
      `}</style>
    </div>
  );
}
